/**
 * Skip / extra / move / blackout application and the applyException reducer
 * (docs/plans/planned-task-schedule.md §3.8 "Exception levels").
 *
 * Effective skip days = task ∪ line ∪ plan. Only the task level is editable;
 * line and plan blackouts arrive as read-only inherited masks.
 *
 * Pinned semantics:
 * - Blackouts beat extras on the same day (a blackout wins over an RDATE).
 * - Order-independent: applying exceptions in any order yields the same set.
 * - Idempotent: re-applying the same change is a no-op.
 */

import { ScheduleChange, ScheduleExceptions, ScheduleExtraDate, ScheduleSkip } from './schedule.types';
import { collectExtraDates, collectSkipDates } from './schedule.compile';

/**
 * Pattern occurrences + extras − skips. Extras are clipped to the window and
 * to effectiveFrom; blackouts win over extras on the same day.
 */
export function applyExceptions(occurrences: readonly string[], exceptions: ScheduleExceptions | undefined, from: string, to: string, effectiveFrom: string): string[] {

    const skipped = new Set(collectSkipDates(exceptions));
    const effective = new Set<string>();

    for (const date of occurrences) {
        if (!skipped.has(date)) {
            effective.add(date);
        }
    }

    for (const extra of collectExtraDates(exceptions)) {
        if (skipped.has(extra) || extra < from || extra > to || extra < effectiveFrom) {
            continue;
        }

        effective.add(extra);
    }

    return Array.from(effective).sort();
}

function sameSkip(a: ScheduleSkip, b: ScheduleSkip): boolean {

    return a.date === b.date && a.from === b.from && a.to === b.to;
}

function withSkip(skips: readonly ScheduleSkip[], skip: ScheduleSkip): ScheduleSkip[] {

    const existing = skips.find(candidate => sameSkip(candidate, skip));

    return existing ? [...skips] : [...skips, skip].sort(compareSkips);
}

function compareSkips(a: ScheduleSkip, b: ScheduleSkip): number {

    return (a.date ?? a.from ?? '').localeCompare(b.date ?? b.from ?? '');
}

function withExtra(extras: readonly ScheduleExtraDate[], extra: ScheduleExtraDate): ScheduleExtraDate[] {

    const existing = extras.find(candidate => candidate.date === extra.date);

    return existing ? [...extras] : [...extras, extra].sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * Reducer over a task's exception set. Never mutates the input; a move is
 * stored as a skip of the source plus an extra on the target.
 */
export function applyExceptionChange(exceptions: ScheduleExceptions | undefined, change: ScheduleChange): ScheduleExceptions {

    const skips = exceptions?.skips ?? [];
    const extras = exceptions?.extras ?? [];
    const next: ScheduleExceptions = { ...exceptions };

    switch (change.type) {
        case 'skipDate':
            next.skips = withSkip(skips, { date: change.date, reason: change.reason });
            break;
        case 'skipRange':
            next.skips = withSkip(skips, { from: change.from, to: change.to, reason: change.reason });
            break;
        case 'extraDate':
            next.extras = withExtra(extras, { date: change.date, note: change.note });
            break;
        case 'moveDate':
            next.skips = withSkip(skips, { date: change.from });
            next.extras = withExtra(extras, { date: change.to });
            break;
    }

    return next;
}
